import { API_BASE } from '../apiBase';
// src/settings/CompanySettings.js
// Superadmin-only company info form. Backs the Contract module's header,
// signature block, and Exhibits A & B.
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${API_BASE}`;

const empty = {
  companyName: 'Stalwart Electric, Inc.',
  legalName: '',
  licenseNumber: '',
  address: { street: '', city: '', state: 'CA', zip: '', country: 'USA' },
  phone: '',
  email: '',
  generalLiability: { carries: true, selfInsured: false, isLLC: false, carrierName: '', carrierPhone: '' },
  workersComp: { exempt: true, carries: false },
  signatory: { name: '', title: 'CEO' },
};

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls = 'w-full bg-gray-950/50 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500';

function CompanySettings() {
  const [settings, setSettings] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const role = localStorage.getItem('role') || 'employee';
  const isSuperadmin = role === 'superadmin';

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/company-settings`, { headers: { Authorization: token } });
        setSettings({ ...empty, ...res.data, address: { ...empty.address, ...(res.data.address || {}) }, generalLiability: { ...empty.generalLiability, ...(res.data.generalLiability || {}) }, workersComp: { ...empty.workersComp, ...(res.data.workersComp || {}) }, signatory: { ...empty.signatory, ...(res.data.signatory || {}) } });
      } catch (err) {
        console.error('Failed to load company settings', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    if (!isSuperadmin) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/company-settings`, settings, { headers: { Authorization: token } });
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (err) {
      setSaveMsg(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (path, value) => {
    setSettings(s => {
      const next = JSON.parse(JSON.stringify(s));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-950 text-zinc-300 p-8">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 space-y-8">
      <div className="flex justify-between items-center border-b border-gray-800 pb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Company Settings</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Used to fill the contract header, signature block, and Exhibits A &amp; B.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saveMsg && <span className="text-sm text-emerald-400">{saveMsg}</span>}
          <button
            onClick={handleSave}
            disabled={!isSuperadmin || saving}
            className="px-5 py-2.5 bg-indigo-700 hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {!isSuperadmin && (
        <div className="bg-amber-900/20 border border-amber-700/40 text-amber-200 rounded-lg p-4 text-sm">
          Read-only — only superadmins can edit company settings.
        </div>
      )}

      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 space-y-5">
        <h2 className="text-xl font-semibold">Identity</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company name">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.companyName} onChange={e => set('companyName', e.target.value)} />
          </Field>
          <Field label="Legal name">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.legalName} onChange={e => set('legalName', e.target.value)} />
          </Field>
          <Field label="CSLB license number">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 space-y-5">
        <h2 className="text-xl font-semibold">Business Address &amp; Contact</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Street" className="md:col-span-2">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.address.street} onChange={e => set('address.street', e.target.value)} />
          </Field>
          <Field label="City">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.address.city} onChange={e => set('address.city', e.target.value)} />
          </Field>
          <Field label="State">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.address.state} onChange={e => set('address.state', e.target.value)} />
          </Field>
          <Field label="ZIP">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.address.zip} onChange={e => set('address.zip', e.target.value)} />
          </Field>
          <Field label="Country">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.address.country} onChange={e => set('address.country', e.target.value)} />
          </Field>
          <Field label="Phone">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.phone} onChange={e => set('phone', e.target.value)} />
          </Field>
          <Field label="Email">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.email} onChange={e => set('email', e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 space-y-5">
        <h2 className="text-xl font-semibold">Exhibit A — General Liability Insurance</h2>
        <div className="space-y-3">
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" disabled={!isSuperadmin} checked={!!settings.generalLiability.carries} onChange={e => set('generalLiability.carries', e.target.checked)} />
            <span>Contractor carries commercial general liability insurance</span>
          </label>
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" disabled={!isSuperadmin} checked={!!settings.generalLiability.selfInsured} onChange={e => set('generalLiability.selfInsured', e.target.checked)} />
            <span>Contractor is self-insured</span>
          </label>
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" disabled={!isSuperadmin} checked={!!settings.generalLiability.isLLC} onChange={e => set('generalLiability.isLLC', e.target.checked)} />
            <span>Contractor is an LLC carrying liability insurance or other security</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Insurance carrier name">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.generalLiability.carrierName} onChange={e => set('generalLiability.carrierName', e.target.value)} />
          </Field>
          <Field label="Insurance carrier phone">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.generalLiability.carrierPhone} onChange={e => set('generalLiability.carrierPhone', e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 space-y-5">
        <h2 className="text-xl font-semibold">Exhibit B — Workers' Compensation</h2>
        <div className="space-y-3">
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" disabled={!isSuperadmin} checked={!!settings.workersComp.exempt} onChange={e => set('workersComp.exempt', e.target.checked)} />
            <span>No employees — exempt from workers' compensation requirements</span>
          </label>
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" disabled={!isSuperadmin} checked={!!settings.workersComp.carries} onChange={e => set('workersComp.carries', e.target.checked)} />
            <span>Carries workers' compensation insurance for all employees</span>
          </label>
        </div>
      </div>

      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 space-y-5">
        <h2 className="text-xl font-semibold">Signatory</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name on signature line">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.signatory.name} onChange={e => set('signatory.name', e.target.value)} />
          </Field>
          <Field label="Title">
            <input className={inputCls} disabled={!isSuperadmin} value={settings.signatory.title} onChange={e => set('signatory.title', e.target.value)} />
          </Field>
        </div>
      </div>

      <CostCategoriesCard canEdit={role === 'admin' || isSuperadmin} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cost Categories (Overhead buckets)
// Self-contained: owns its own fetch/CRUD against /cost-categories, so it can
// be lifted out or dropped into any page without touching the rest of Settings.
// ---------------------------------------------------------------------------
function CostCategoriesCard({ canEdit }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const hdr = () => ({ headers: { Authorization: localStorage.getItem('token') } });

  const load = async () => {
    try {
      const res = await axios.get(`${API}/cost-categories`, hdr());
      setCats(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setErr('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/cost-categories`, { name: newName.trim(), code: newCode.trim() }, hdr());
      setNewName(''); setNewCode('');
      await load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Add failed');
    } finally { setBusy(false); }
  };

  const rename = async (id, name, code) => {
    try {
      await axios.put(`${API}/cost-categories/${id}`, { name, code }, hdr());
      setCats(cs => cs.map(c => c._id === id ? { ...c, name, code } : c));
    } catch (_) { setErr('Save failed'); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cats.length) return;
    const reordered = [...cats];
    const [item] = reordered.splice(idx, 1);
    reordered.splice(j, 0, item);
    setCats(reordered);
    // Persist new order values sequentially.
    try {
      await Promise.all(reordered.map((c, i) =>
        c.order === i ? null : axios.put(`${API}/cost-categories/${c._id}`, { order: i }, hdr())
      ));
    } catch (_) { setErr('Reorder failed'); }
  };

  const archive = async (id) => {
    if (!window.confirm('Archive this category? Existing expenses keep their label.')) return;
    try {
      await axios.delete(`${API}/cost-categories/${id}`, hdr());
      setCats(cs => cs.filter(c => c._id !== id));
    } catch (_) { setErr('Archive failed'); }
  };

  return (
    <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Cost Categories (Overhead)</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Buckets for expenses that aren't job-costed to a project — Office, Software, Gas, etc.
        </p>
      </div>

      {err && <div className="text-sm text-rose-400">{err}</div>}

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <div className="space-y-2">
          {cats.map((c, idx) => (
            <div key={c._id} className="flex items-center gap-2">
              <input
                className="w-20 bg-gray-950/50 border border-zinc-800 rounded-lg px-2 py-1.5 text-sm text-zinc-300 focus:outline-none focus:border-indigo-500 disabled:opacity-60"
                disabled={!canEdit}
                value={c.code || ''}
                onChange={e => setCats(cs => cs.map(x => x._id === c._id ? { ...x, code: e.target.value } : x))}
                onBlur={e => canEdit && rename(c._id, c.name, e.target.value)}
                placeholder="code"
              />
              <input
                className="flex-1 bg-gray-950/50 border border-zinc-800 rounded-lg px-3 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-indigo-500 disabled:opacity-60"
                disabled={!canEdit}
                value={c.name}
                onChange={e => setCats(cs => cs.map(x => x._id === c._id ? { ...x, name: e.target.value } : x))}
                onBlur={e => canEdit && rename(c._id, e.target.value, c.code)}
              />
              {canEdit && (
                <>
                  <button onClick={() => move(idx, -1)} disabled={idx === 0}
                    className="px-2 py-1 text-zinc-400 hover:text-zinc-100 disabled:opacity-30" title="Move up">↑</button>
                  <button onClick={() => move(idx, 1)} disabled={idx === cats.length - 1}
                    className="px-2 py-1 text-zinc-400 hover:text-zinc-100 disabled:opacity-30" title="Move down">↓</button>
                  <button onClick={() => archive(c._id)}
                    className="px-2 py-1 text-rose-400/70 hover:text-rose-400" title="Archive">✕</button>
                </>
              )}
            </div>
          ))}
          {cats.length === 0 && <div className="text-sm text-zinc-600">No categories yet.</div>}
        </div>
      )}

      {canEdit && (
        <div className="flex items-center gap-2 pt-2 border-t border-gray-800/50">
          <input
            className="w-20 bg-gray-950/50 border border-zinc-800 rounded-lg px-2 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-indigo-500"
            value={newCode} onChange={e => setNewCode(e.target.value)} placeholder="code"
          />
          <input
            className="flex-1 bg-gray-950/50 border border-zinc-800 rounded-lg px-3 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-indigo-500"
            value={newName} onChange={e => setNewName(e.target.value)} placeholder="New category name…"
            onKeyDown={e => e.key === 'Enter' && add()}
          />
          <button onClick={add} disabled={busy || !newName.trim()}
            className="px-4 py-1.5 bg-indigo-700 hover:bg-indigo-600 disabled:opacity-40 text-white rounded-lg text-sm font-medium">
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
      )}

      {!canEdit && (
        <div className="text-xs text-zinc-600">Read-only — only admins can manage categories.</div>
      )}
    </div>
  );
}

export default CompanySettings;
